if (!customElements.get('jk-cart-offers')) {
  // A single timeline persists while Dawn replaces cart section markup.
  const motion = { tier: null, node: null, frame: 0, line: 0, lamps: [0, 0, 0, 0], run: null };
  const clamp = (value) => Math.min(1, Math.max(0, value));
  const ease = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };
  const progress = (tier) => tier ? (tier - .5) / 4 : 0;
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function celebrate(node) {
    const drawer = node.closest('cart-drawer');
    if (!drawer?.classList.contains('active') || !node.getClientRects().length || reduced()) return;
    const panel = drawer.querySelector('.drawer__inner');
    const rect = panel.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    drawer.querySelector('.jk-cart-confetti')?.remove();
    const layer = document.createElement('div');
    layer.className = 'jk-cart-confetti';
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
    drawer.append(layer);
    const colors = ['#878b95', '#c7c9ce', '#e6e6e8', '#45474b', '#f7f7f7'];
    let finished = 0;
    for (let index = 0; index < 42; index++) {
      const piece = document.createElement('i');
      const ribbon = index % 4 === 0;
      const startX = Math.random() * rect.width;
      const drift = (Math.random() - .5) * rect.width * .6;
      const rotation = Math.random() * 360;
      Object.assign(piece.style, { left: `${startX}px`, background: colors[index % colors.length], width: `${ribbon ? 4 : 5 + Math.random() * 4}px`, height: `${ribbon ? 22 : 8 + Math.random() * 7}px`, borderRadius: ribbon ? '2px' : '1px' });
      layer.append(piece);
      const animation = piece.animate([
        { transform: `translate3d(0,-25px,0) rotate(${rotation}deg) rotateY(0deg)`, opacity: 0 },
        { transform: `translate3d(${drift * .15}px,${rect.height * .08}px,0) rotate(${rotation + 90}deg) rotateY(160deg)`, opacity: .85, offset: .15 },
        { transform: `translate3d(${drift}px,${rect.height + 30}px,0) rotate(${rotation + 500}deg) rotateY(680deg)`, opacity: 0 },
      ], { duration: 2200 + Math.random() * 1300, delay: Math.random() * 360, easing: 'cubic-bezier(.25,.5,.5,1)', fill: 'both' });
      animation.onfinish = () => { if (++finished === 42) layer.remove(); };
    }
  }

  function paint(now) {
    const run = motion.run;
    if (run) {
      const age = Math.max(0, now - run.started);
      motion.line = run.line + (progress(run.tier) - run.line) * ease(age / 780);
      motion.lamps = run.lamps.map((from, index) => {
        const to = index < run.tier ? 1 : 0;
        // The new flame follows the advancing line; extinguish starts immediately.
        const delay = to > from ? 400 + Math.max(0, index - run.fromTier) * 110 : 0;
        return from + (to - from) * ease((age - delay) / (to ? 520 : 380));
      });
      if (!run.celebrated && run.tier > run.fromTier && age >= 650 && motion.node?.isConnected) {
        run.celebrated = true;
        celebrate(motion.node);
      }
      if (age >= 1250) motion.run = null;
    }
    const node = motion.node;
    if (node?.isConnected) {
      node.querySelector('.jk-cart-steps__fill').style.transform = `scaleX(${motion.line})`;
      node.querySelectorAll('.jk-cart-step').forEach((step, index) => {
        const level = motion.lamps[index];
        step.style.setProperty('--jk-light', level.toFixed(4));
        step.style.setProperty('--jk-flame-scale', (.55 + level * .45).toFixed(4));
        if (run && index >= run.tier && run.lamps[index] > 0) {
          const t = clamp((now - run.started - 150) / 850);
          const smoke = step.querySelector('.jk-diya-smoke');
          smoke.style.opacity = String(Math.sin(t * Math.PI) * .35);
          smoke.style.transform = `translateY(${-t * 13}px) scale(${.8 + t * .35})`;
        }
      });
    }
    motion.frame = motion.run ? requestAnimationFrame(paint) : 0;
  }

  customElements.define('jk-cart-offers', class extends HTMLElement {
    previewRemoval(quantity) {
      const previous = Number(this.dataset.quantity) || 0;
      const heading = this.querySelector('h3');
      const originalHeading = heading.innerHTML;
      const apply = (count) => {
        const tier = Math.max(0, Math.min(4, count));
        this.dataset.tier = String(tier);
        this.querySelectorAll('.jk-cart-step').forEach((step, index) => {
          step.classList.toggle('is-unlocked', index < tier);
          if (index + 1 === tier) step.setAttribute('aria-current', 'step');
          else step.removeAttribute('aria-current');
          const label = step.querySelector('.visually-hidden');
          if (label) label.textContent = index < tier ? 'Unlocked' : 'Locked';
        });
        this.connectedCallback();
        // A rollback is not a newly earned offer.
        if (motion.run) motion.run.celebrated = true;
        return tier;
      };
      const tier = apply(Math.max(0, previous - quantity));
      heading.textContent = tier === 4 ? 'All diyas lit. 20% extra off unlocked!' : `Add 1 more item for ${(tier + 1) * 5}% extra off`;
      let emptyAnimation;
      if (previous - quantity <= 0) {
        if (this.animate && !reduced()) {
          const styles = getComputedStyle(this);
          this.classList.add('jk-cart-offers-removing');
          emptyAnimation = this.animate([
            { height: `${this.getBoundingClientRect().height}px`, opacity: 1, paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom, marginBottom: styles.marginBottom },
            { height: `${this.getBoundingClientRect().height}px`, opacity: 0, paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom, marginBottom: styles.marginBottom, offset: .45 },
            { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px', marginBottom: '0px' },
          ], { duration: 380, easing: 'ease-out', fill: 'forwards' });
          emptyAnimation.finished.then(() => { this.hidden = true; }).catch(() => {});
        } else {
          this.hidden = true;
        }
      }
      return () => {
        if (!this.isConnected) return;
        emptyAnimation?.cancel();
        this.hidden = false;
        this.classList.remove('jk-cart-offers-removing');
        apply(previous);
        heading.innerHTML = originalHeading;
      };
    }
    connectedCallback() {
      const tier = Math.max(0, Math.min(4, Number(this.dataset.tier) || 0));
      const now = performance.now();
      if (motion.run) paint(now);
      cancelAnimationFrame(motion.frame);
      motion.frame = 0;
      if (motion.tier === null || reduced()) {
        motion.line = progress(tier);
        motion.lamps = [0, 1, 2, 3].map((index) => index < tier ? 1 : 0);
        motion.run = null;
      } else if (motion.tier !== tier) {
        motion.run = { fromTier: motion.tier, tier, started: now, line: motion.line, lamps: [...motion.lamps], celebrated: false };
        this.closest('cart-drawer')?.querySelector('.jk-cart-confetti')?.remove();
      }
      motion.tier = tier;
      motion.node = this;
      paint(now);
    }
    disconnectedCallback() {
      if (motion.node === this) motion.node = null;
    }
  });
}

if (!window.jkCartUpsellInitialized) {
  window.jkCartUpsellInitialized = true;
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-jk-upsell-add]');
    if (!button || button.disabled) return;
    const drawer = button.closest('cart-drawer');
    if (!drawer) return;
    const error = button.closest('.jk-cart-upsell').querySelector('[data-jk-upsell-error]');
    const label = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = '•••';
    error.hidden = true;
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: button.dataset.jkUpsellAdd, quantity: Number(button.dataset.quantity) || 1 }],
          sections: drawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Could not add this item. Please try again.');
      if (typeof publish === 'function') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jk-upsell', cartData: result });
      drawer.setActiveElement(document.querySelector('#cart-icon-bubble'));
      drawer.classList.remove('is-empty');
      await drawer.renderContents(result);
    } catch (failure) {
      error.textContent = failure.message || 'Could not add this item. Please try again.';
      error.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = label;
    }
  });
}
